import React, { useContext, useEffect, useRef, useState } from 'react';
import { ComboboxItem, Popover, Select } from '@mantine/core';
import { RichTextEditor } from '@mantine/tiptap';
import { Row, Tooltip } from 'antd';
import Text from 'antd/lib/typography/Text';
import Title from 'antd/lib/typography/Title';
import Upload, { UploadProps } from 'antd/lib/upload';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { UploadFile } from 'antd/lib/upload/interface';
import { apiV2 } from 'services/api';
import { UploadOutlined } from '@ant-design/icons';
import { Bitrix24Context } from 'contexts/legacy/Bitrix24Context';
import type { Auths } from 'interfaces/bitrix24';

import '../../../styles/createMarketingCampaignModal.css';
import { LanguageContext } from 'contexts/LanguageContext';

interface CampaignMessageProps {
	value: string;
	files: UploadFile[];
	isStatic: string;
	required?: boolean;
	message?: string;
	onChangeValue: (value: string) => void;
	onChangeFiles: (files: UploadFile[]) => void;
}

function CampaignMessage(props: CampaignMessageProps) {
	const [auths, setAuths] = useState<Auths>({ access_token: '', refresh_token: '' });
	const [domain, setDomain] = useState<string>();
	const [variables, setVariables] = useState<string[]>([]);
	const [variablesPopoverOpened, setVariablesPopoverOpened] = useState(false);

	const bitrix24 = useContext(Bitrix24Context);
	const { dictionary } = useContext(LanguageContext);

	const { value, files } = props;
	const { onChangeFiles, onChangeValue, message } = props;
	const uploaderRef = useRef(null);
	const uploadProps = {
		className: 'avatar-uploader',
		onRemove: (file) => {
			if (!files) return;
			const index = files.indexOf(file);
			const newFileList = files.slice();
			newFileList.splice(index, 1);
			onChangeFiles(newFileList);
		},
		beforeUpload: (file) => {
			onChangeFiles([file]);
			return true;
		},
		fileList: files,
	} as UploadProps;

	const editor = useEditor({
		extensions: [
			StarterKit,
			Placeholder.configure({ placeholder: dictionary.write_your_message_here }),
		],
		content: value,
		onUpdate({ editor }) {
			const htmlMessage = editor.getHTML() !== '<p></p>' ? editor.getHTML() : '';
			onChangeValue(htmlMessage);
		},
	});

	const errorStatus = useRef<string | undefined>(undefined);

	useEffect(() => {
		editor?.chain().focus();
	}, []);

	useEffect(() => {
		if (!domain) return;
		fetchVariables().then((response) => {
			setVariables(response);
		});
	}, [domain]);

	useEffect(() => {
		if (!bitrix24.infos.domain) return;
		setDomain(bitrix24.infos.domain);
		setAuths(bitrix24.auth);
	}, [bitrix24.auth, bitrix24.infos.domain]);

	useEffect(() => {
		errorStatus.current = message ? '#f5222d' : undefined;
		editor?.chain().focus();
	}, [message]);

	async function fetchVariables() {
		return await apiV2
			.get(`/variables/${domain}`, {
				headers: {
					'Access-Token': auths.access_token,
					'Refresh-Token': auths.refresh_token,
				},
			})
			.then((response) => response.data);
	}

	function handleOpenFileSelector() {
		const uploader = uploaderRef.current as any;
		uploader.upload.uploader.fileInput.click();
	}

	function insertStringAtCursor(value: string | null, option: ComboboxItem) {
		if (!editor) return;
		const { state } = editor;
		const { from, to } = state.selection;
		const transaction = state.tr.insertText(`{{${value}}}`, from, to);
		editor.view.dispatch(transaction);
		editor.chain().focus();
		setVariablesPopoverOpened(false);
	}

	return (
		<>
			<Row style={{ height: 75, paddingTop: '2em' }}>
				<Title level={2}>
					<Text type="gray-9">{dictionary.write_your_campaign_message}</Text>
				</Title>
			</Row>
			<Row className="pa-0" style={{ display: 'flex', alignItems: 'center', height: 180 }}>
				<RichTextEditor
					editor={editor}
					styles={{
						root: {
							width: '100%',
							borderColor: errorStatus.current,
						},
						content: {
							height: 100,
							textAlign: 'left',
							overflowY: 'auto',
						},
					}}
				>
					<RichTextEditor.Toolbar sticky stickyOffset={60}>
						<RichTextEditor.ControlsGroup>
							<RichTextEditor.Bold />
							<RichTextEditor.Italic />
							<RichTextEditor.Strikethrough />
							<RichTextEditor.Code />
						</RichTextEditor.ControlsGroup>

						<RichTextEditor.ControlsGroup>
							<Tooltip title={dictionary.upload_file_max_size}>
								<RichTextEditor.Control onClick={handleOpenFileSelector}>
									<UploadOutlined />
								</RichTextEditor.Control>
							</Tooltip>
							<Popover
								opened={variablesPopoverOpened}
								zIndex={1000}
								width={300}
								position="bottom"
								withArrow
								shadow="md"
							>
								{!props.isStatic && (
									<>
										<Popover.Target>
											<RichTextEditor.Control
												onClick={() => setVariablesPopoverOpened(!variablesPopoverOpened)}
												styles={{
													control: {
														fontFamily: 'emoji',
													},
												}}
											>
												<Text>{'{ }'}</Text>
											</RichTextEditor.Control>
										</Popover.Target>
										<Popover.Dropdown>
											<Select
												placeholder={dictionary.select_variable}
												data={variables}
												searchable
												autoFocus
												onChange={insertStringAtCursor}
												comboboxProps={{
													withinPortal: false,
												}}
											/>
										</Popover.Dropdown>
									</>
								)}
							</Popover>
						</RichTextEditor.ControlsGroup>

						<RichTextEditor.ControlsGroup>
							<RichTextEditor.ClearFormatting />
						</RichTextEditor.ControlsGroup>

						<RichTextEditor.ControlsGroup className="ml-auto">
							<RichTextEditor.Undo />
							<RichTextEditor.Redo />
						</RichTextEditor.ControlsGroup>
					</RichTextEditor.Toolbar>

					<RichTextEditor.Content />
				</RichTextEditor>
			</Row>
			{message && (
				<Row style={{ height: 50, textAlign: 'start' }}>
					<Text type="danger">{message}</Text>
				</Row>
			)}
			<Row className="pb-2" style={{ minHeight: 75 }}>
				<Upload ref={uploaderRef} {...uploadProps} />
			</Row>
		</>
	);
}

export default CampaignMessage;
